import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useAuthStore } from '../../stores/auth';

/**
 * Landing page for the emailed sign-in link. The token is only spent when the
 * person clicks the button (a POST), so mail scanners that prefetch the link
 * can't use it up first (#170).
 */
function VerifyPage() {
  const { token } = Route.useSearch();
  const verify = useAuthStore((s) => s.verify);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onContinue = async () => {
    setBusy(true);
    setError(null);
    try {
      await verify(token ?? '');
      const me = useAuthStore.getState().me;
      navigate({ to: me?.needs_terms ? '/terms' : '/' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8, px: 2 }}>
      <Paper variant="outlined" sx={{ p: 4, width: '100%', maxWidth: 400 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Sign in to mollusk
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error} <Link href="/login">Request a new link</Link>
          </Alert>
        ) : null}
        <Button
          variant="contained"
          fullWidth
          disabled={busy || !token}
          onClick={() => void onContinue()}
        >
          Continue
        </Button>
      </Paper>
    </Box>
  );
}

export const Route = createFileRoute('/auth/verify')({
  validateSearch: (s: Record<string, unknown>): { token?: string } =>
    typeof s.token === 'string' ? { token: s.token } : {},
  component: VerifyPage,
});
